<template>
  <div class="controller-points-list">
    <!-- Header Card -->
    <q-card flat bordered class="q-mb-md">
      <q-card-section>
        <div class="row items-center">
          <q-icon name="memory" size="xl" color="green" class="q-mr-md" />
          <div class="col">
            <div class="text-h6">{{ pointsListData?.controller?.name || 'Controller' }}</div>
            <div class="text-caption text-grey">{{ pointsListData?.controller?.model || 'DDC Controller' }}</div>
          </div>
          <q-badge color="primary" class="q-mr-md">
            {{ pointsListData?.summary?.allocated || 0 }} / {{ pointsListData?.summary?.total || 0 }} allocated
          </q-badge>
          <q-btn color="primary" icon="picture_as_pdf" label="Export PDF" :loading="exporting" @click="exportPdf" />
          <q-btn flat color="grey" icon="settings" label="Commissioning" class="q-ml-sm" @click="showCommissioningStub = true" />
        </div>
      </q-card-section>
    </q-card>

    <!-- Points Table grouped by I/O type -->
    <q-card flat bordered class="q-mb-md">
      <q-table
        :rows="allTerminals"
        :columns="columns"
        row-key="terminal"
        :loading="loading"
        flat
        dense
        :pagination="{ rowsPerPage: 0 }"
        hide-pagination
      >
        <!-- Group headers for each I/O type -->
        <template #body="props">
          <!-- Show type header when type changes -->
          <q-tr v-if="shouldShowTypeHeader(props.rowIndex)" class="bg-grey-2">
            <q-td colspan="9" class="text-weight-bold">
              {{ getTypeHeaderLabel(props.row.io_type) }}
              <span class="text-caption text-grey q-ml-sm">(drag to swap allocations)</span>
            </q-td>
          </q-tr>
          <q-tr
            :props="props"
            draggable="true"
            :class="{
              'dragged-row': isDraggedRow(props.row),
              'drop-target': dropTarget === props.row.terminal,
              'valid-drop-target': isValidDropTarget(props.row),
            }"
            @dragstart="onDragStart($event, props.row)"
            @dragover="onDragOver($event, props.row)"
            @dragleave="onDragLeave"
            @dragend="onDragEnd"
            @drop="onDrop($event, props.row)"
          >
            <q-td key="terminal" class="cursor-grab">
              <q-icon name="drag_indicator" size="xs" color="grey" class="q-mr-xs" />
              <q-badge :color="getTerminalColor(props.row.io_type)">{{ props.row.terminal }}</q-badge>
            </q-td>
            <q-td key="signal_type">{{ props.row.signal_type || '-' }}</q-td>
            <q-td key="point_name">{{ props.row.point_name || '-' }}</q-td>
            <q-td key="comments">{{ props.row.comments || '-' }}</q-td>
            <q-td key="end_device">{{ props.row.end_device || '-' }}</q-td>
            <q-td key="checked">
              <q-icon
                :name="props.row.commissioning?.checked ? 'check_box' : 'check_box_outline_blank'"
                :color="props.row.commissioning?.checked ? 'positive' : 'grey'"
              />
            </q-td>
            <q-td key="operation_check">
              <q-icon
                :name="props.row.commissioning?.operation_check ? 'check_box' : 'check_box_outline_blank'"
                :color="props.row.commissioning?.operation_check ? 'positive' : 'grey'"
              />
            </q-td>
            <q-td key="calibration">
              <q-icon
                :name="props.row.commissioning?.calibration ? 'check_box' : 'check_box_outline_blank'"
                :color="props.row.commissioning?.calibration ? 'positive' : 'grey'"
              />
            </q-td>
            <q-td key="comm_comments">
              <span class="text-caption">{{ props.row.commissioning?.comments || '-' }}</span>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </q-card>

    <!-- Controller Configuration Card -->
    <q-card flat bordered>
      <q-card-section>
        <div class="text-subtitle2 q-mb-md">Controller Configuration</div>
        <div class="row q-col-gutter-md">
          <!-- Firmware Version -->
          <div class="col-12 col-md-4">
            <q-input
              v-model="localConfig.firmware_version"
              label="Firmware Version"
              outlined
              dense
              @update:model-value="emitConfigUpdate"
            />
          </div>

          <!-- Protocol Selection -->
          <div class="col-12 col-md-4">
            <q-select
              v-model="localConfig.protocol"
              label="Communication Protocol"
              :options="protocolOptions"
              outlined
              dense
              emit-value
              map-options
              @update:model-value="emitConfigUpdate"
            />
          </div>

          <!-- Sedona Fields -->
          <template v-if="localConfig.protocol === 'Sedona'">
            <div class="col-12 col-md-4">
              <q-input
                v-model="localConfig.sedona_ip_address"
                label="IP Address"
                placeholder="192.168.1.100"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-input
                v-model.number="localConfig.sedona_http_port"
                label="HTTP Port"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-input
                v-model.number="localConfig.sedona_tcom_port"
                label="TCOM Port"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
          </template>

          <!-- BACnet/IP Fields -->
          <template v-if="localConfig.protocol === 'BACnet/IP'">
            <div class="col-12 col-md-4">
              <q-input
                v-model="localConfig.bacnet_ip_address"
                label="IP Address"
                placeholder="192.168.1.100"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.bacnet_device_instance"
                label="Device Instance"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.bacnet_udp_port"
                label="UDP Port"
                placeholder="47808"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
          </template>

          <!-- BACnet MS/TP Fields -->
          <template v-if="localConfig.protocol === 'BACnet MS/TP'">
            <div class="col-12 col-md-3">
              <q-input
                v-model.number="localConfig.bacnet_device_instance"
                label="Device Instance"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-input
                v-model.number="localConfig.bacnet_mstp_address"
                label="MS/TP Address"
                placeholder="1-127"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-select
                v-model="localConfig.bacnet_mstp_baud"
                label="Baud Rate"
                :options="baudRateOptions"
                outlined
                dense
                emit-value
                map-options
                @update:model-value="emitConfigUpdate"
              />
            </div>
          </template>

          <!-- Modbus TCP Fields -->
          <template v-if="localConfig.protocol === 'Modbus TCP'">
            <div class="col-12 col-md-4">
              <q-input
                v-model="localConfig.modbus_ip_address"
                label="IP Address"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.modbus_tcp_port"
                label="TCP Port"
                placeholder="502"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.modbus_unit_id"
                label="Unit ID"
                placeholder="1-247"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
          </template>

          <!-- Modbus RTU Fields -->
          <template v-if="localConfig.protocol === 'Modbus RTU'">
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.modbus_rtu_address"
                label="Slave Address"
                placeholder="1-247"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-input
                v-model.number="localConfig.modbus_unit_id"
                label="Unit ID"
                placeholder="1-247"
                type="number"
                outlined
                dense
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-select
                v-model="localConfig.modbus_rtu_baud"
                label="Baud Rate"
                :options="modbusRtuBaudOptions"
                outlined
                dense
                emit-value
                map-options
                @update:model-value="emitConfigUpdate"
              />
            </div>
            <div class="col-12 col-md-2">
              <q-select
                v-model="localConfig.modbus_rtu_parity"
                label="Parity"
                :options="parityOptions"
                outlined
                dense
                emit-value
                map-options
                @update:model-value="emitConfigUpdate"
              />
            </div>
          </template>
        </div>
      </q-card-section>
    </q-card>

    <!-- Commissioning Stub Dialog -->
    <q-dialog v-model="showCommissioningStub">
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center">
          <q-icon name="construction" size="lg" color="warning" class="q-mr-md" />
          <div>
            <div class="text-h6">Coming Soon</div>
            <div class="text-caption text-grey">Commissioning functionality will be available in the Project Management section.</div>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="OK" color="primary" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { useControllerIO, getTerminalColor as getTerminalColorHelper } from '@/composables/brick-schema/documentation/useControllerIO';

  const $q = useQuasar();

  const props = defineProps({
    equipmentId: {
      type: [Number, String],
      required: true,
    },
    siteId: {
      type: [Number, String],
      required: true,
    },
    categoryData: {
      type: Object,
      default: () => ({}),
    },
  });

  const emit = defineEmits(['update:categoryData']);

  // Use composable
  const {
    pointsListData,
    loading,
    exporting,
    assigning,
    loadPointsList,
    exportPointsListPdf,
    swapTerminals,
  } = useControllerIO(props.siteId, props.equipmentId);

  // Local state
  const showCommissioningStub = ref(false);
  const draggedTerminal = ref(null);
  const dropTarget = ref(null);

  // Configuration state with defaults
  const localConfig = ref({
    firmware_version: '',
    protocol: 'Sedona',
    sedona_ip_address: '',
    sedona_http_port: 80,
    sedona_tcom_port: 5021,
    bacnet_ip_address: '',
    bacnet_device_instance: null,
    bacnet_udp_port: 47808,
    bacnet_mstp_address: null,
    bacnet_mstp_baud: '38400',
    modbus_ip_address: '',
    modbus_tcp_port: 502,
    modbus_unit_id: null,
    modbus_rtu_address: null,
    modbus_rtu_baud: '9600',
    modbus_rtu_parity: 'None',
  });

  // Protocol options
  const protocolOptions = [
    { label: 'Sedona', value: 'Sedona' },
    { label: 'BACnet/IP', value: 'BACnet/IP' },
    { label: 'BACnet MS/TP', value: 'BACnet MS/TP' },
    { label: 'Modbus TCP', value: 'Modbus TCP' },
    { label: 'Modbus RTU', value: 'Modbus RTU' },
  ];

  const baudRateOptions = [
    { label: '9600', value: '9600' },
    { label: '19200', value: '19200' },
    { label: '38400', value: '38400' },
    { label: '76800', value: '76800' },
    { label: '115200', value: '115200' },
  ];

  const modbusRtuBaudOptions = [
    { label: '9600', value: '9600' },
    { label: '19200', value: '19200' },
    { label: '38400', value: '38400' },
    { label: '57600', value: '57600' },
    { label: '115200', value: '115200' },
  ];

  const parityOptions = [
    { label: 'None', value: 'None' },
    { label: 'Even', value: 'Even' },
    { label: 'Odd', value: 'Odd' },
  ];

  // Initialize from props
  onMounted(() => {
    if (props.categoryData && Object.keys(props.categoryData).length > 0) {
      localConfig.value = { ...localConfig.value, ...props.categoryData };
    }
  });

  // Watch for external changes to categoryData
  watch(() => props.categoryData, (newVal) => {
    if (newVal && Object.keys(newVal).length > 0) {
      localConfig.value = { ...localConfig.value, ...newVal };
    }
  }, { deep: true });

  // Emit changes to parent
  function emitConfigUpdate() {
    emit('update:categoryData', { ...localConfig.value });
  }

  // Table columns
  const columns = [
    { name: 'terminal', label: 'IO', field: 'terminal', align: 'left' },
    { name: 'signal_type', label: 'Type', field: 'signal_type', align: 'left' },
    { name: 'point_name', label: 'Point Name', field: 'point_name', align: 'left' },
    { name: 'comments', label: 'Comments', field: 'comments', align: 'left' },
    { name: 'end_device', label: 'End Device', field: 'end_device', align: 'left' },
    { name: 'checked', label: 'Checked', align: 'center' },
    { name: 'operation_check', label: 'Op Check', align: 'center' },
    { name: 'calibration', label: 'Calibration', align: 'center' },
    { name: 'comm_comments', label: 'Comm. Comments', field: (row) => row.commissioning?.comments, align: 'left' },
  ];

  // Computed
  const allTerminals = computed(() => {
    if (!pointsListData.value?.terminals) {return [];}

    // The API returns terminals as a flat array already sorted by type and index
    // Each terminal has io_type property
    return pointsListData.value.terminals;
  });

  // Methods
  function getTerminalColor(type) {
    const colors = {
      UI: 'purple',
      UO: 'teal',
      AI: 'blue',
      AO: 'green',
      DI: 'orange',
      DO: 'red',
    };
    return colors[type?.toUpperCase()] || 'grey';
  }

  function shouldShowTypeHeader(rowIndex) {
    if (rowIndex === 0) {return true;}

    const currentRow = allTerminals.value[rowIndex];
    const previousRow = allTerminals.value[rowIndex - 1];

    return currentRow.io_type !== previousRow.io_type;
  }

  function getTypeHeaderLabel(type) {
    const labels = {
      UI: 'Universal Inputs',
      UO: 'Universal Outputs',
      AI: 'Analog Inputs',
      AO: 'Analog Outputs',
      DI: 'Digital Inputs',
      DO: 'Digital Outputs',
    };
    return labels[type?.toUpperCase()] || type;
  }

  async function exportPdf() {
    try {
      await exportPointsListPdf();
      $q.notify({
        type: 'positive',
        message: 'PDF exported successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to export PDF',
      });
    }
  }

  // Drag and drop handlers
  function onDragStart(event, terminal) {
    draggedTerminal.value = terminal;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', terminal.terminal);
  }

  function onDragOver(event, terminal) {
    // Only allow drop if same I/O type
    if (draggedTerminal.value && draggedTerminal.value.io_type === terminal.io_type) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      dropTarget.value = terminal.terminal;
    }
  }

  function onDragLeave() {
    dropTarget.value = null;
  }

  function onDragEnd() {
    draggedTerminal.value = null;
    dropTarget.value = null;
  }

  async function onDrop(event, terminal) {
    event.preventDefault();
    dropTarget.value = null;

    if (!draggedTerminal.value || draggedTerminal.value.terminal === terminal.terminal) {
      draggedTerminal.value = null;
      return;
    }

    // Verify same I/O type
    if (draggedTerminal.value.io_type !== terminal.io_type) {
      $q.notify({
        type: 'warning',
        message: 'Can only swap terminals of the same I/O type',
      });
      draggedTerminal.value = null;
      return;
    }

    // Save terminal names before async operation (refs may change during reload)
    const terminalAName = draggedTerminal.value.terminal;
    const terminalBName = terminal.terminal;

    // Clear dragged state before async to prevent stale reference issues
    draggedTerminal.value = null;

    try {
      await swapTerminals(terminalAName, terminalBName);
      $q.notify({
        type: 'positive',
        message: `Swapped ${terminalAName} with ${terminalBName}`,
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to swap terminals',
      });
    }
  }

  function isValidDropTarget(terminal) {
    return draggedTerminal.value &&
      draggedTerminal.value.terminal !== terminal.terminal &&
      draggedTerminal.value.io_type === terminal.io_type;
  }

  function isDraggedRow(terminal) {
    return draggedTerminal.value?.terminal === terminal.terminal;
  }

  // Lifecycle
  onMounted(async () => {
    try {
      await loadPointsList();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to load points list',
      });
    }
  });
</script>

<style scoped>
.controller-points-list {
  display: flex;
  flex-direction: column;
}

/* Drag and drop styles */
.cursor-grab {
  cursor: grab;
}

.cursor-grab:active {
  cursor: grabbing;
}

.dragged-row {
  opacity: 0.5;
  background-color: #e3f2fd !important;
}

.drop-target {
  background-color: #c8e6c9 !important;
  outline: 2px dashed #4caf50;
  outline-offset: -2px;
}

.valid-drop-target {
  background-color: #f3e5f5;
}

.valid-drop-target:hover {
  background-color: #e1bee7;
}
</style>
